import {ApiResponse} from "apisauce";
import {RawAxiosResponseHeaders, AxiosRequestConfig} from "axios";
import Alert from "react-s-alert";
import {logErrorToSentry} from "../../utils/sentry";
import {isTokenValid} from "../../utils/token";
import {
  AUTH_ERROR_STATUS,
  HANDLED_PROBLEMS,
  LOGGED_PROBLEMS,
  IGNORED_RESPONSE_STATUSES,
  Config,
} from "./consts";
import {v4} from "uuid";
import {getCommonAuthorizationHeader} from "./selectors";
import {AUTH_SIGN_IN_URL} from "../AuthService";

export const getRefreshedToken = (headers: RawAxiosResponseHeaders) => {
  const token = headers?.["x-refreshed-jwt"];

  return typeof token === "string" ? token.split(" ")[1] : null;
};

// Check if the authorization header contains a valid token
const hasValidAuthToken = (authHeader: string | null | undefined): boolean => {
  if (!authHeader || authHeader.trim() === "") {
    return false;
  }

  const trimmed = authHeader.trim().toLowerCase();
  return !trimmed.startsWith("bearer null") && !trimmed.startsWith("bearer undefined");
};

export const isUnauthorized = (status: number) =>
  AUTH_ERROR_STATUS.UNAUTHORIZED === status;

export const isForbidden = (status: number) =>
  AUTH_ERROR_STATUS.FORBIDDEN === status;

// Transform the response from the API client to handle errors and other issues
export function responseTransform(response: ApiResponse<any>, config?: Config) {
  if (!response.problem) {
    const refreshedToken = getRefreshedToken(response.headers);

    if (isTokenValid(refreshedToken)) {
      config?.setToken(refreshedToken);
    }
  }

  if (LOGGED_PROBLEMS.includes(response.problem)) {
    logErrorToSentry(response.originalError, {response});
  }

  // Skip alerting for sign-in calls
  const reqConfig = response.config as any as AxiosRequestConfig;
  const isSignInCall = Boolean(reqConfig?.url?.includes(AUTH_SIGN_IN_URL));

  if (
    IGNORED_RESPONSE_STATUSES.includes(response.status) ||
    isSignInCall ||
    config?.skipUnauthorizedHandling?.(
      response.config as any as AxiosRequestConfig,
    )
  ) {
    // do nothing
  } else if (isUnauthorized(response.status)) {
    config?.logout();
    Alert.info("Your session has expired. Try to login again.");

    throw response;
  } else if (isForbidden(response.status)) {
    config?.logout();
    Alert.info("You are not allowed to see this page.");
  } else {
    if (HANDLED_PROBLEMS.includes(response.problem)) {
      Alert.error(
        "An error has occurred. Please contact your Clerk or Administrator.",
      );
    }
  }

  if (response.problem) {
    throw response;
  }
}

// Transform the request to add headers and other information
export const asyncRequestTransform = async (
  request: any,
  authorization?: string,
) => {
  request.headers = request.headers || {};
  request.headers["X-Correlation-ID"] = v4();

  // get the common authorization header when possible (e.g., when redux state is available.
  // Otherwise, use the token passed in the request like in the case when admission is started from email.)
  const authHeader = authorization ?? getCommonAuthorizationHeader();

  if (hasValidAuthToken(authHeader)) {
    request.headers.Authorization = authHeader;
  }

  await Promise.resolve();
};
